import React, { useState, useRef } from 'react';
import { Box, Typography, Card, CardContent, Button, Dialog, DialogContent, IconButton, Stack, Tabs, Tab } from '@mui/material';
import { motion } from 'framer-motion';
import { staggerContainer, staggerItem } from '../motion/presets';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const webProjects = [
  { id: 'lafacil', url: 'https://lafacil-1.web.app/' },
  { id: 'awtar', url: 'https://awtar.org/' },
  { id: 'rotaEhealth', url: 'https://rota.ehealth.sa/' },
  { id: 'informaticsDoctor', url: 'https://informatics.doctor/' },
  { id: 'awtarKsaV2', url: 'https://real-estate-ksa-v2.web.app/' },
  { id: 'realEstateKsaV1', url: 'https://real-estate-ksa-v1.web.app/' },
  { id: 'realEstateKsaV3', url: 'https://real-estate-ksa-v3.web.app/' },
];

const mobileProjects = [
  { id: 'attendance', gallery: 'android' },
  { id: 'naqrah', gallery: 'naqrah' },
];

const androidImages = [
  require('../assets/images/history.jpg'),
  require('../assets/images/holidays.jpg'),
  require('../assets/images/home.jpg'),
  require('../assets/images/leaves.jpg'),
];


const naqrahImages = [
  require('../assets/images/naqrah/1.jpg'),
  require('../assets/images/naqrah/2.jpg'),
  require('../assets/images/naqrah/3.jpg'),
  require('../assets/images/naqrah/4.jpg'),
  require('../assets/images/naqrah/5.jpg'),
  require('../assets/images/naqrah/6.jpg'),
  require('../assets/images/naqrah/7.jpg'),
];

const fontStack = `'Fira Mono', 'Menlo', 'Consolas', 'Liberation Mono', 'monospace'`;

const projectPanelAlignSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
};

const projectPanelSx = {
  boxSizing: 'border-box',
  width: { xs: '98vw', sm: 800, md: 1000, lg: 1200, xl: 1800, '2xl': 2000 },
  maxWidth: '90vw',
  borderRadius: 3,
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)',
  overflow: 'hidden',
  background: 'rgba(36, 12, 44, 0.98)',
  border: '1.5px solid #77216f',
  position: 'relative',
};

const projectPanelBarSx = {
  display: 'flex',
  alignItems: 'center',
  height: 32,
  px: 2,
  background: 'linear-gradient(90deg, #77216f 60%, #e95420 100%)',
  borderBottom: '1px solid #77216f',
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
};

const projectCardsGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  gap: { xs: 2, sm: 2.5, md: 3 },
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
};

const projectTextSx = (isAr) => ({
  direction: isAr ? 'rtl' : 'ltr',
  textAlign: isAr ? 'right' : 'left',
  width: '100%',
});

const ProjectCard = ({ title, description, active, isAr, children }) => (
  <Card
    component={motion.div}
    variants={staggerItem}
    whileHover={{ y: -6, scale: 1.02 }}
    transition={{ type: 'spring', stiffness: 360, damping: 22 }}
    elevation={0}
    sx={{
      width: '100%',
      minHeight: { xs: 200, sm: 220 },
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      borderRadius: 2,
      background: 'rgba(24, 28, 32, 0.92)',
      border: '1.5px solid',
      borderColor: active ? '#25d366' : 'rgba(119, 33, 111, 0.85)',
      boxShadow: active ? '0 0 0 1px #25d36644' : 'none',
      color: '#fff',
      fontFamily: fontStack,
      transition: 'border-color 0.2s, box-shadow 0.2s',
      '&:hover': {
        borderColor: '#e95420',
      },
    }}
  >
    <CardContent sx={{ flex: 1, pb: 1 }}>
      <Typography sx={{ fontSize: 12, mb: 1.5, lineHeight: 1.5 }}>
        <Box component="span" sx={{ color: '#25d366' }}>abdulaziz@ubuntu</Box>
        <Box component="span" sx={{ color: '#61afef' }}>:~/projects$</Box>
        <Box component="span" sx={{ color: '#fff' }}> cat {title.replace(/\s+/g, '_').toLowerCase()}.md</Box>
      </Typography>
      <Typography
        variant="h6"
        fontWeight={700}
        gutterBottom
        sx={{
          color: '#e95420',
          fontSize: { xs: 15, sm: 17 },
          fontFamily: 'Ubuntu, sans-serif',
          ...projectTextSx(isAr),
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'rgba(255,255,255,0.88)',
          fontSize: { xs: 12, sm: 13 },
          lineHeight: 1.6,
          fontFamily: 'Ubuntu, sans-serif',
          whiteSpace: 'pre-line',
          ...projectTextSx(isAr),
        }}
      >
        {description}
      </Typography>
    </CardContent>
    <Stack
      direction="row"
      flexWrap="wrap"
      spacing={1}
      useFlexGap
      sx={{ px: 2, pb: 2, pt: 0, gap: 1 }}
    >
      {children}
    </Stack>
  </Card>
);

const Projects = () => {
  const [iframeOpen, setIframeOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryType, setGalleryType] = useState('android'); // 'android' or 'naqrah'
  const [selectedProject, setSelectedProject] = useState(0);
  const [previewClosed, setPreviewClosed] = useState(false);
  const previewRef = useRef(null);
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');

  const projectTitle = (id) => t(`projects.items.${id}.title`);
  const projectDescription = (id) => t(`projects.items.${id}.description`);


  const openIframe = (url) => {
    setIframeUrl(url);
    setIframeOpen(true);
  };
  const closeIframe = () => {
    setIframeOpen(false);
    setIframeUrl('');
  };

  const openGallery = (idx = 0, type = 'android') => {
    setGalleryIndex(idx);
    setGalleryType(type);
    setGalleryOpen(true);
  };
  const closeGallery = () => setGalleryOpen(false);

  const handleTabChange = (e, newValue) => {
    setSelectedProject(newValue);
    setPreviewClosed(false);
  };

  const handleClosePreview = () => setPreviewClosed(true);
  const handleOpenPreview = () => setPreviewClosed(false);

  const handleShowAbove = (idx) => {
    setSelectedProject(idx);
    setPreviewClosed(false);
    setTimeout(() => {
      if (previewRef.current) {
        previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  return (
      <Box id="projects" sx={{ py: { xs: 5, sm: 8, xl: 12 }, width: '100%', maxWidth: '100%', mx: 'auto', boxSizing: 'border-box' }}>
        <Box
          component={motion.h2}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          sx={{
            m: 0,
            mb: { xs: 3, sm: 4 },
            fontWeight: 700,
            textAlign: 'center',
            color: '#e95420',
            fontSize: { xs: 28, sm: 36, xl: 48 },
            direction: isAr ? 'rtl' : 'ltr',
            fontFamily: 'Ubuntu, Montserrat, sans-serif',
          }}
        >
          {t('projects.title')}
        </Box>
        {/* Big Live Preview Window */}
        <Box ref={previewRef} sx={{ ...projectPanelAlignSx, mb: { xs: 4, md: 6 } }}>
          <Box sx={{
            ...projectPanelSx,
            height: { xs: '70vh', sm: 700, md: 800, lg: 800, xl: 1600 },
            minHeight: { xs: 220, sm: 320, md: 480, lg: 600, xl: 800 },
            overflowX: { xs: 'auto', sm: 'unset' },
          }}>
            {/* Ubuntu Terminal Bar */}
            <Box sx={projectPanelBarSx}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#e95420', mr: 1, boxShadow: '0 0 2px #e95420' }} onClick={() => handleClosePreview()} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f6c915', mr: 1, boxShadow: '0 0 2px #f6c915' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#25d366', boxShadow: '0 0 4px #25d366' }} onClick={() => handleOpenPreview()} />
              <Box sx={{ flexGrow: 1 }} />
              {/* {previewClosed ? (
                <Button size="small" sx={{ color: '#fff', fontWeight: 600 }} onClick={handleOpenPreview}>Open Preview</Button>
              ) : (
                <IconButton onClick={handleClosePreview} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
              )} */}
            </Box>
            {/* Tabs for switching projects */}
            <Tabs
              value={selectedProject}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ bgcolor: 'rgba(36,12,44,0.98)', color: '#fff', borderBottom: '1px solid #77216f', minHeight: 44, minWidth: 0, width: '100%' }}
              TabIndicatorProps={{ style: { background: '#e95420' } }}
            >
              {webProjects.map((project) => (
                <Tab
                  key={project.id}
                  label={projectTitle(project.id)}
                  sx={{ color: '#fff', fontWeight: 600, minHeight: 44, fontSize: { xs: 14, sm: 16, xl: 20 }, minWidth: 100, width: '100%', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                />
              ))}
            </Tabs>
            {/* Iframe Preview */}
            {!previewClosed && (
              <Box sx={{ width: '100%', height: '100%', bgcolor: '#181c20', position: 'relative', minWidth: 0 }}>
                <iframe
                  src={webProjects[selectedProject].url}
                  title={`${projectTitle(webProjects[selectedProject].id)} Preview`}
                  width="100%"
                  height="100%"
                  style={{ border: 'none', minHeight: 180, width: '100%', maxWidth: '100vw', height: '100%' }}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                />
              </Box>
            )}
            {previewClosed && (
              <Box sx={{ width: '100%', height: { xs: 180, sm: 320, md: 420, lg: 540, xl: 700 }, bgcolor: '#181c20', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: { xs: 16, sm: 22, xl: 32 }, opacity: 0.7 }}>
                {t('projects.previewClosed')}
              </Box>
            )}
          </Box>
        </Box>
        {/* Project Cards */}
        <Box sx={projectPanelAlignSx}>
          <Box sx={projectPanelSx}>
            <Box sx={projectPanelBarSx}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#e95420', mr: 1, boxShadow: '0 0 2px #e95420' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f6c915', mr: 1, boxShadow: '0 0 2px #f6c915' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#25d366', boxShadow: '0 0 4px #25d366' }} />
              <Box sx={{ flexGrow: 1 }} />
              <Typography sx={{ color: '#fff', fontFamily: fontStack, fontSize: 14, opacity: 0.75 }}>
                ls ~/projects
              </Typography>
            </Box>
            <Box
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                width: '100%',
                boxSizing: 'border-box',
                bgcolor: 'rgba(36, 12, 44, 0.98)',
                fontFamily: fontStack,
              }}
            >
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.65)',
                  fontSize: 13,
                  mb: 2,
                  direction: isAr ? 'rtl' : 'ltr',
                  textAlign: isAr ? 'right' : 'left',
                }}
              >
                {t('projects.gridHint')}
              </Typography>
              <Box
                component={motion.div}
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                sx={projectCardsGridSx}
              >
                {webProjects.map((project, idx) => (
                  <ProjectCard
                    key={project.id}
                    title={projectTitle(project.id)}
                    description={projectDescription(project.id)}
                    active={selectedProject === idx && !previewClosed}
                    isAr={isAr}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        bgcolor: '#e95420',
                        color: '#fff',
                        fontWeight: 600,
                        boxShadow: 'none',
                        fontFamily: fontStack,
                        fontSize: 12,
                        '&:hover': { bgcolor: '#c34113' },
                      }}
                      onClick={() => handleShowAbove(idx)}
                    >
                      {t('projects.actions.preview')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: '#61afef',
                        color: '#61afef',
                        fontWeight: 600,
                        fontFamily: fontStack,
                        fontSize: 12,
                        '&:hover': { borderColor: '#e95420', color: '#e95420' },
                      }}
                      endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('projects.actions.open')}
                    </Button>
                  </ProjectCard>
                ))}
                {mobileProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    title={projectTitle(project.id)}
                    description={projectDescription(project.id)}
                    isAr={isAr}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        bgcolor: '#e95420',
                        color: '#fff',
                        fontWeight: 600,
                        boxShadow: 'none',
                        fontFamily: fontStack,
                        fontSize: 12,
                        '&:hover': { bgcolor: '#c34113' },
                      }}
                      onClick={() => openGallery(0, project.gallery)}
                    >
                      {t('projects.actions.screenshots')}
                    </Button>
                  </ProjectCard>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Iframe Modal (for legacy cards, can be removed if not needed) */}
        <Dialog open={iframeOpen} onClose={closeIframe} maxWidth="lg" fullWidth>
          <DialogContent sx={{ p: 0, position: 'relative', bgcolor: '#181c20' }}>
            <IconButton onClick={closeIframe} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, color: '#fff' }}>
              <CloseIcon />
            </IconButton>
            <Box sx={{ width: '100%', height: '70vh', bgcolor: '#181c20' }}>
              <iframe
                src={iframeUrl}
                title="Project Preview"
                width="100%"
                height="100%"
                style={{ border: 'none', minHeight: 400 }}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              />
            </Box>
          </DialogContent>
        </Dialog>
        {/* Gallery Modal */}
        <Dialog open={galleryOpen} onClose={closeGallery} maxWidth="sm" fullWidth>
          <DialogContent sx={{ p: 0, position: 'relative', bgcolor: '#181c20' }}>
            <IconButton onClick={closeGallery} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, color: '#fff' }}>
              <CloseIcon />
            </IconButton>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
              <img
                src={galleryType === 'naqrah' ? naqrahImages[galleryIndex] : androidImages[galleryIndex]}
                alt={`Screenshot ${galleryIndex + 1}`}
                style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8, boxShadow: '0 2px 16px #0008' }}
              />
              <Stack direction="row" spacing={1} mt={2}>
                {(galleryType === 'naqrah' ? naqrahImages : androidImages).map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`Thumb ${idx + 1}`}
                    style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, border: idx === galleryIndex ? '2px solid #e95420' : '2px solid transparent', cursor: 'pointer' }}
                    onClick={() => setGalleryIndex(idx)}
                  />
                ))}
              </Stack>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
  );
};

export default Projects; 